import { Button } from '@calizahq/react-components-v2'
import clsx from 'clsx'
import React, { ChangeEvent, useMemo, useState } from 'react'

import { MarketSegmentFilter } from '@/components/ListFilter'
import { FilterActions, useFiltersContext } from '@/context'

import { LegacyDropdown } from '../LegacyDropdown/LegacyDropdown'
import { HomeQualityCheckStatusFilter } from './HomeQualityChecks/HomeQualityCheckStatusFilter'
import HomesFilters from './HomesFilters'
import JobsFilters from './JobsFilters'
import PropertiesFilters from './PropertiesFilters'
import { LengthOfStayFilter } from './ReservationFilters/LengthOfStayFilter'
import { ReservationMoveInFilter } from './ReservationFilters/ReservationMoveInFilter'
import { ReservationMoveOutFilter } from './ReservationFilters/ReservationMoveOutFilter'
import ReservationsFilters from './ReservationFilters/ReservationsFilters'

export enum FilterTypes {
  HOMES = 'homes',
  HOME_QUALITY_CHECKS = 'homeQualityChecks',
  JOBS = 'jobs',
  MEMBER = 'member',
  PROPERTIES = 'properties',
  RESERVATIONS = 'reservations',
}

type Props = {
  hideFilters?: boolean
  hideLengthOfStayFilter?: boolean
  hideMarketSegment?: boolean
  hideReservationMoveInFilter?: boolean
  hideReservationMoveOutFilter?: boolean
  hideSearch?: boolean
  listType?: FilterTypes
  title: string
}

const ListFilter: React.FC<Props> = ({
  hideFilters = false,
  listType,
  title,
  hideLengthOfStayFilter = true,
  hideMarketSegment = false,
  hideReservationMoveInFilter = true,
  hideReservationMoveOutFilter = true,
  hideSearch = false,
}) => {
  const [{ jobsFilters, homesFilters, propertiesFilters, reservationsFilters, searchString }, FilterDispatch] =
    useFiltersContext()
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)

  const selectedFilterCount = useMemo(() => {
    switch (listType) {
      case FilterTypes.RESERVATIONS:
        return Object.keys(reservationsFilters).filter((k) => !!reservationsFilters[k])?.length
      case FilterTypes.PROPERTIES:
        return Object.keys(propertiesFilters).filter((k) => !!propertiesFilters[k])?.length
      case FilterTypes.HOMES:
        return Object.keys(homesFilters).filter((k) => !!homesFilters[k])?.length
      case FilterTypes.JOBS:
        return Object.keys(jobsFilters).filter((k) => !!jobsFilters[k])?.length
      default:
        return 0
    }
  }, [homesFilters, listType, jobsFilters, propertiesFilters, reservationsFilters])

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    FilterDispatch(FilterActions.setSearchString({ val: e.target.value }))
  }

  const renderFilterButton = () => {
    const showActiveFilters = selectedFilterCount > 0
    return (
      <Button className="whitespace-nowrap bg-action-secondary px-3 text-label-sm " size="small" variant="secondary">
        <div className="flex">
          Filter
          {showActiveFilters && <CountBubble>{selectedFilterCount}</CountBubble>}
        </div>
      </Button>
    )
  }

  const handleClose = () => {
    setIsFiltersOpen(false)
  }

  const renderFilterOptions = () => {
    switch (listType) {
      case FilterTypes.RESERVATIONS:
        return <ReservationsFilters multiSelect={true} onClose={handleClose} />
      case FilterTypes.PROPERTIES:
        return <PropertiesFilters onClose={handleClose} />
      case FilterTypes.HOMES:
        return <HomesFilters multiSelect={true} onClose={handleClose} />
      case FilterTypes.JOBS:
        return <JobsFilters multiSelect={true} onClose={handleClose} />
      default:
        return null
    }
  }

  return (
    <div className="mb-4 flex w-full flex-row flex-wrap items-center md:mb-6">
      <h2 className="mr-4 flex-1 text-header-sm md:mb-0">{title}</h2>
      {!hideMarketSegment && (
        <div className="md:mr-4">
          <MarketSegmentFilter />
        </div>
      )}

      {!hideReservationMoveInFilter && <ReservationMoveInFilter />}
      {!hideReservationMoveOutFilter && <ReservationMoveOutFilter />}
      {!hideLengthOfStayFilter && <LengthOfStayFilter />}

      {listType === FilterTypes.HOME_QUALITY_CHECKS ? <HomeQualityCheckStatusFilter /> : null}

      <>
        {!hideSearch && (
          <input
            autoComplete="off"
            className="my-2 mr-4 flex-1 rounded-lg border-2 border-border-secondary p-2 focus:outline-none md:my-0 md:w-auto md:flex-0"
            onChange={onSearch}
            placeholder="Search"
            value={searchString || ''}
          />
        )}
        {!hideFilters && (
          <LegacyDropdown
            className="my-2 md:my-0"
            direction="left"
            isOpen={isFiltersOpen}
            renderToggle={renderFilterButton}
            setIsOpen={setIsFiltersOpen}
          >
            {isFiltersOpen ? renderFilterOptions() : null}
          </LegacyDropdown>
        )}
      </>
    </div>
  )
}

const CountBubble = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    {...props}
    className={clsx('flex items-center justify-center bg-action-primary text-white ml-2', className)}
    style={{ borderRadius: '50%', width: '15px', height: '15px', fontSize: '10px' }}
  />
)

export default ListFilter
